// front/src/preview-mode/graph/aggregates.ts
import type { PreviewFunnel, PreviewSession } from "./types";

// --- Event-name-based step counting (used by analytics_funnel_query / InsightsPage) ---

export function countStep(
  sessions: readonly PreviewSession[],
  stepNames: readonly string[],
  stepIndex: number,
): number {
  // A session reaches stepIndex if it fired the events at [0..stepIndex] in order.
  let count = 0;
  outer: for (const session of sessions) {
    let cursor = 0;
    for (const evt of session.events) {
      if (evt.eventName === stepNames[cursor]) {
        cursor++;
        if (cursor > stepIndex) {
          count++;
          continue outer;
        }
      }
    }
  }
  return count;
}

// --- URL-pattern matching (used by funnel_query / FunnelsPage) ---

// Match a path against a glob-style URL pattern: `*` wildcards, full-string match.
export function matchesUrlPattern(path: string, pattern: string): boolean {
  // Escape regex metacharacters except `*`, then replace `*` with `.*`.
  const regexSrc =
    "^" +
    pattern.replace(/[.+?^${}()|[\]\\]/g, "\\$&").replace(/\*/g, ".*") +
    "$";
  return new RegExp(regexSrc).test(path);
}

export function matchesAnyPattern(
  path: string,
  patterns: readonly string[],
): boolean {
  return patterns.some((p) => matchesUrlPattern(path, p));
}

// --- URL-pattern-based step counting ---

// Count sessions that progress through each URL-pattern-based step in order.
// A step is "reached" when a page_view event fires whose path matches any of
// that step's urlPatterns, after the previous step was reached.
export function countPatternStep(
  sessions: readonly PreviewSession[],
  steps: readonly { urlPatterns: readonly string[] }[],
  stepIndex: number,
): number {
  let count = 0;
  outer: for (const session of sessions) {
    let cursor = 0;
    for (const evt of session.events) {
      if (evt.eventName !== "page_view") continue;
      if (matchesAnyPattern(evt.path, steps[cursor].urlPatterns)) {
        cursor++;
        if (cursor > stepIndex) {
          count++;
          continue outer;
        }
      }
    }
  }
  return count;
}

export function computePatternStepCounts(
  sessions: readonly PreviewSession[],
  steps: readonly { urlPatterns: readonly string[] }[],
): number[] {
  return steps.map((_, i) => countPatternStep(sessions, steps, i));
}

// --- Funnel construction ---

export function buildFunnels(
  sessions: readonly PreviewSession[],
  now: number,
): PreviewFunnel[] {
  const funnels: PreviewFunnel[] = [
    {
      id: "funnel-appointment-booking",
      name: "Appointment Booking Funnel",
      description:
        "Primary conversion path from service-line page to a completed appointment booking.",
      steps: [
        {
          eventName: "page_view",
          label: "Viewed a service page",
          urlPatterns: ["*/services/*"],
        },
        {
          eventName: "page_view",
          label: "Viewed appointments page",
          urlPatterns: ["*/appointments"],
        },
        {
          eventName: "page_view",
          label: "Viewed booking form",
          urlPatterns: ["*/appointments/new"],
        },
        {
          eventName: "page_view",
          label: "Completed booking",
          urlPatterns: ["*/appointments/new/confirmation*"],
        },
      ],
      stepCounts: [],
      createdAt: now - 30 * 24 * 60 * 60 * 1000,
      updatedAt: now,
    },
    {
      id: "funnel-insurance-eligibility",
      name: "Insurance Eligibility Path",
      description: "Users researching insurance coverage.",
      steps: [
        {
          eventName: "page_view",
          label: "Viewed insurance page",
          urlPatterns: ["*/insurance*"],
        },
        {
          eventName: "page_view",
          label: "Viewed appointments page",
          urlPatterns: ["*/appointments"],
        },
        {
          eventName: "page_view",
          label: "Viewed booking form",
          urlPatterns: ["*/appointments/new"],
        },
      ],
      stepCounts: [],
      createdAt: now - 20 * 24 * 60 * 60 * 1000,
      updatedAt: now,
    },
    {
      id: "funnel-provider-discovery",
      name: "Provider Discovery",
      description: "Users browsing providers and starting an inquiry.",
      steps: [
        {
          eventName: "page_view",
          label: "Viewed providers index",
          urlPatterns: ["*/providers"],
        },
        {
          eventName: "page_view",
          label: "Viewed provider detail",
          urlPatterns: ["*/providers/*"],
        },
        {
          eventName: "page_view",
          label: "Started inquiry",
          urlPatterns: ["*/appointments/new*"],
        },
      ],
      stepCounts: [],
      createdAt: now - 10 * 24 * 60 * 60 * 1000,
      updatedAt: now,
    },
    {
      // Single-service-line funnel anchored at the homepage. Cardiology is
      // 1 of 5 service detail pages (~8/111 outgoing weight from `/`), so
      // only a small fraction of homepage visitors discover it before
      // continuing — the resulting step-0 → step-1 dropoff sits well above
      // the 40% threshold that surfaces FunnelsPage's "Dig Deeper" CTA.
      // We keep this funnel in the demo dataset so the preview always has a
      // funnel that exercises that CTA.
      id: "funnel-cardiology-booking",
      name: "Homepage → Cardiology Booking",
      description:
        "How homepage visitors discover the cardiology service line and progress through the booking flow.",
      steps: [
        {
          eventName: "page_view",
          label: "Visited homepage",
          urlPatterns: ["*/"],
        },
        {
          eventName: "page_view",
          label: "Viewed cardiology page",
          urlPatterns: ["*/services/cardiology"],
        },
        {
          eventName: "page_view",
          label: "Viewed appointments page",
          urlPatterns: ["*/appointments"],
        },
        {
          eventName: "page_view",
          label: "Started booking",
          urlPatterns: ["*/appointments/new"],
        },
      ],
      stepCounts: [],
      createdAt: now - 7 * 24 * 60 * 60 * 1000,
      updatedAt: now,
    },
  ];

  for (const funnel of funnels) {
    funnel.stepCounts = computePatternStepCounts(
      sessions,
      funnel.steps.map((s) => ({ urlPatterns: s.urlPatterns })),
    );
  }

  return funnels;
}
